import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRightIcon,
  CheckIcon,
  ClockIcon,
  PaintBrushIcon,
  ShieldCheckIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import '../dialify.css';

const benefits = [
  {
    icon: SparklesIcon,
    title: 'Curated every week',
    copy: 'Fresh collections for work, weekends, movement and everything between.',
  },
  {
    icon: PaintBrushIcon,
    title: 'Made to feel personal',
    copy: 'Refined colors, type and complications that make every glance feel considered.',
  },
  {
    icon: ClockIcon,
    title: 'Ready in moments',
    copy: 'Pick a dial, follow the guided setup and give your watch a completely new mood.',
  },
  {
    icon: ShieldCheckIcon,
    title: 'Simple and secure',
    copy: 'Clear subscription terms, protected checkout and control whenever you need it.',
  },
];

const faces = [
  '/images/figma_wf7/face_17_preview.png',
  '/images/figma_wf7/face_23_preview-2.png',
  '/images/figma_wf7/face_31_preview.png',
  '/images/figma_wf7/face_33_preview.png',
];

const DialifyLanding: React.FC = () => {
  return (
    <div className="dialify-landing min-h-screen overflow-hidden">
      <div className="dialify-grid pointer-events-none absolute inset-x-0 top-0 h-[760px]" />

      <header className="relative z-20 px-5 pt-5 sm:px-8">
        <nav className="dialify-glass mx-auto flex max-w-6xl items-center justify-between rounded-full px-5 py-3.5 sm:px-7">
          <Link to="/" className="flex items-center gap-3" aria-label="Dialify home">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#315F86] text-sm font-black text-[#FDF9F2]">
              D
            </span>
            <span className="text-lg font-extrabold tracking-[-0.03em] text-[#1B3651]">dialify</span>
          </Link>
          <div className="hidden items-center gap-7 text-sm font-semibold text-[#526B82] md:flex">
            <a href="#collection" className="transition-colors hover:text-[#1B3651]">Collection</a>
            <a href="#why" className="transition-colors hover:text-[#1B3651]">Why Dialify</a>
            <Link to="/privacy" className="transition-colors hover:text-[#1B3651]">Privacy</Link>
          </div>
          <Link
            to="/wf/wf5"
            className="rounded-full bg-[#315F86] px-5 py-2.5 text-sm font-bold text-[#FDF9F2] transition-all hover:-translate-y-0.5 hover:bg-[#254B6A]"
          >
            Find my dial
          </Link>
        </nav>
      </header>

      <main className="relative z-10">
        <section className="mx-auto grid max-w-6xl items-center gap-12 px-6 pb-20 pt-20 lg:grid-cols-[1.02fr_.98fr] lg:px-8 lg:pb-28 lg:pt-28">
          <div className="max-w-2xl">
            <div className="mb-7 inline-flex items-center gap-2 rounded-full border border-[#AFC2D2] bg-[#FBF7F0]/80 px-4 py-2 text-xs font-bold uppercase tracking-[0.14em] text-[#315F86]">
              <SparklesIcon className="h-4 w-4" />
              A fresh face for every mood
            </div>
            <h1 className="text-5xl font-black leading-[0.96] tracking-[-0.055em] text-[#1B3651] sm:text-6xl lg:text-7xl">
              Your watch,
              <span className="block bg-gradient-to-r from-[#315F86] via-[#557FA2] to-[#7B9DB9] bg-clip-text text-transparent">
                beautifully dialed.
              </span>
            </h1>
            <p className="mt-7 max-w-xl text-lg leading-8 text-[#5C7083] sm:text-xl">
              Dialify brings together expressive smartwatch faces, thoughtful details and effortless setup—so every glance feels more like you.
            </p>
            <div className="mt-9 flex flex-col gap-3 sm:flex-row">
              <Link
                to="/wf/wf5"
                className="dialify-primary inline-flex items-center justify-center gap-2 rounded-full bg-[#315F86] px-7 py-4 text-base font-bold text-[#FDF9F2] transition-all hover:-translate-y-1 hover:bg-[#254B6A]"
              >
                Discover your style <ArrowRightIcon className="h-5 w-5" />
              </Link>
              <a
                href="#collection"
                className="inline-flex items-center justify-center rounded-full border border-[#AFC2D2] bg-[#FBF7F0]/70 px-7 py-4 text-base font-bold text-[#1B3651] transition-all hover:bg-[#FBF7F0]"
              >
                Browse the collection
              </a>
            </div>
            <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm font-medium text-[#607589]">
              {['Curated designs', 'Guided setup', 'Secure checkout'].map((item) => (
                <span key={item} className="flex items-center gap-2">
                  <CheckIcon className="h-4 w-4 stroke-[3] text-[#315F86]" /> {item}
                </span>
              ))}
            </div>
          </div>

          <div className="relative mx-auto w-full max-w-[520px]">
            <div className="absolute -left-10 top-14 h-52 w-52 rounded-full bg-[#B9CFE1]/70 blur-3xl" />
            <div className="absolute -right-8 bottom-12 h-56 w-56 rounded-full bg-white/80 blur-3xl" />
            <div className="dialify-glass dialify-orbit relative rounded-[42px] p-5 sm:p-7">
              <div className="rounded-[32px] bg-gradient-to-br from-[#DCE8F2] via-[#F9F5EE] to-[#DFD3C2] p-5 sm:p-8">
                <img
                  src="/images/wf7_step1_img.png"
                  alt="A collection of premium smartwatch faces"
                  className="mx-auto w-full drop-shadow-[0_28px_28px_rgba(27,54,81,0.18)]"
                />
              </div>
              <div className="mt-5 flex items-center justify-between px-2">
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.15em] text-[#6D8499]">Today’s edit</p>
                  <p className="mt-1 text-lg font-extrabold text-[#1B3651]">Soft blue, warm sand</p>
                </div>
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#315F86] text-[#FDF9F2]">
                  <ArrowRightIcon className="h-5 w-5" />
                </span>
              </div>
            </div>
          </div>
        </section>

        <section id="collection" className="border-y border-[#B8C9D7]/60 bg-[#DCE8F2]/70 px-6 py-20 lg:px-8 lg:py-24">
          <div className="mx-auto max-w-6xl">
            <div className="flex flex-col justify-between gap-5 sm:flex-row sm:items-end">
              <div>
                <p className="text-sm font-bold uppercase tracking-[0.16em] text-[#315F86]">The collection</p>
                <h2 className="mt-3 max-w-xl text-4xl font-black tracking-[-0.045em] text-[#1B3651] sm:text-5xl">Designed for more than telling time.</h2>
              </div>
              <p className="max-w-sm text-base leading-7 text-[#5C7083]">From calm neutrals to expressive color, find the face that fits the moment.</p>
            </div>
            <div className="mt-12 grid grid-cols-2 gap-4 md:grid-cols-4">
              {faces.map((face, index) => (
                <div key={face} className="dialify-glass group rounded-[28px] p-3 transition-transform duration-300 hover:-translate-y-2 sm:p-4">
                  <div className="overflow-hidden rounded-[22px] bg-[#EEF3F7]">
                    <img src={face} alt={`Dialify watch face ${index + 1}`} className="aspect-[.84] h-full w-full object-contain transition-transform duration-500 group-hover:scale-105" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="why" className="px-6 py-20 lg:px-8 lg:py-28">
          <div className="mx-auto max-w-6xl">
            <div className="mx-auto max-w-2xl text-center">
              <p className="text-sm font-bold uppercase tracking-[0.16em] text-[#315F86]">Why Dialify</p>
              <h2 className="mt-3 text-4xl font-black tracking-[-0.045em] text-[#1B3651] sm:text-5xl">Small details. A completely new feeling.</h2>
            </div>
            <div className="mt-12 grid gap-5 md:grid-cols-2">
              {benefits.map(({ icon: Icon, title, copy }) => (
                <article key={title} className="dialify-glass rounded-[30px] p-7 sm:p-8">
                  <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#315F86] text-[#FDF9F2]">
                    <Icon className="h-6 w-6" />
                  </span>
                  <h3 className="mt-6 text-2xl font-extrabold tracking-[-0.025em] text-[#1B3651]">{title}</h3>
                  <p className="mt-3 max-w-md leading-7 text-[#5C7083]">{copy}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="px-6 pb-20 lg:px-8 lg:pb-28">
          <div className="mx-auto max-w-6xl overflow-hidden rounded-[38px] bg-[#1B3651] px-7 py-12 text-center text-[#FDF9F2] shadow-[0_30px_80px_rgba(27,54,81,0.24)] sm:px-12 sm:py-16">
            <p className="text-sm font-bold uppercase tracking-[0.16em] text-[#B8CEE0]">Ready when you are</p>
            <h2 className="mx-auto mt-4 max-w-2xl text-4xl font-black tracking-[-0.045em] sm:text-5xl">Make the next glance your favorite.</h2>
            <Link to="/wf/wf5" className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#F3ECDF] px-7 py-4 font-bold text-[#1B3651] transition-transform hover:-translate-y-1">
              Find my perfect dial <ArrowRightIcon className="h-5 w-5" />
            </Link>
          </div>
        </section>
      </main>

      <footer className="border-t border-[#B8C9D7]/60 px-6 py-10 lg:px-8">
        <div className="mx-auto flex max-w-6xl flex-col gap-6 text-sm text-[#5C7083] sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#315F86] text-xs font-black text-[#FDF9F2]">D</span>
            <span>© {new Date().getFullYear()} Dialify</span>
          </div>
          <div className="flex flex-wrap gap-6 font-semibold">
            <Link to="/terms" className="hover:text-[#1B3651]">Terms</Link>
            <Link to="/privacy" className="hover:text-[#1B3651]">Privacy</Link>
            <Link to="/cancellation" className="hover:text-[#1B3651]">Cancellation</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default DialifyLanding;
